import { FC, useEffect, useState, useRef, useMemo } from "react"
import { debounce } from "lodash";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins"
import { SysOptionsCommandPanel } from "./components/SysOptionsCommandPanel"
import { DocumentGridStateType } from "../../../@types/documents";
import styles from '../../styles/index.module.scss'
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import useGridFilter from "../../../system/hooks/useGridFilter";
import { DocumentType, LoadingStatus } from "../../../@types/enumsGlobal";
import { ISysOptionsViewDTO } from "../../../libs/coreapi-dto/service/sysOptions";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { SysOptionsDataProvider } from "../../../Services/DataProviders/SysOptionsDataProvider";
import { TabsPanel } from "../../../components/tabs";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import classNames from "classnames";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import { SysOptionsEditModal } from "./components/editModalWindow/SysOptionsEditModal";
import { checkLockStatus } from "../../CommonHelperFunctions";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../components/spiner/Spinner";

const MIN_SEARCH_LENGTH = 3;

export const SysOptionsPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext()
    const tabCtx = useTabsContext();
    const pluginCtx = usePluginContext();
    const sysOptionsDataProvider = new SysOptionsDataProvider(appContext.coreApiService)

    const [editModal, setEditModal] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.InProcess);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [data, setData] = useState<ISysOptionsViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    
    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.plugin.permission as IPermission, userContext.userPermission);
    const isRealRefresh = useRef(false);

    // Локализация
    const { t } = useTranslation();
    const baseT = (value: string) => t("sysOptions." + value);

    const [searchValue, setSearchValue] = useState<string>("");

    function searchSysOptionsDescriptionFilter(value: string) {
        if (value.length >= MIN_SEARCH_LENGTH) {
            dispatchGridFilter({
                type: "addColumnFilter", 
                payload: {
                    name: "description",
                    value: `%${value}%`,
                    operator: "Like",
                } as IGridColumnFilter,
            });
        } else if (value.length === 0) {
            dispatchGridFilter({ type: "deleteColumnFilter", payload: "description" });
        }
    }


    const debouncedSearch = useMemo(
        () => debounce(searchSysOptionsDescriptionFilter, 1250),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        
        if (value.length === 0) {
            debouncedSearch.cancel();
            dispatchGridFilter({ type: "deleteColumnFilter", payload: "description" });
        }
    };

    useEffect(() => {
        if (searchValue !== '' && searchValue.length >= MIN_SEARCH_LENGTH) {
            debouncedSearch(searchValue);
        }
    }, [searchValue]);

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);
    
    const handleRefresh = () => {
        setSelectedItem(undefined);
        isRealRefresh.current = true;
        setViewState('refresh');
        
        if (searchValue.length >= MIN_SEARCH_LENGTH) {
            searchSysOptionsDescriptionFilter(searchValue);
        }
    };

    useEffect(() => {
        if (viewState == 'refresh') {
            setViewState('view');
            return
        }
        setIsSubmitting(true);

        sysOptionsDataProvider.getView(gridFilter, (e, totalCount)=> {
            if (e.length > 0) {
                setData(e);
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
                setViewState('view');
                setIsSubmitting(false);
            } else {
                setLoadingStatus(LoadingStatus.NoData);
            } 
        })
    }, [viewState, gridFilter])


    useEffect(() => {
        if (tabCtx.currentTab?.title === `${baseT("tabName")}` /*'Системные настройки'*/) {
            setShowDetailsTabs(true);
        } else {
            setShowDetailsTabs(false);
        }
        pluginCtx.masterGrid.onUnselectEvent();
        setShowDetailsTabs(true);
    }, [tabCtx.currentTab]);

    const isDisabledEdit = (): boolean => {
        if (!selectedItem)
            return true;

        const isReadOnly = data.find(c => c.idGlobal === selectedItem.idGlobal)?.isReadOnly
        if (isReadOnly)
            return isReadOnly;

        return false;
    }
    
    const getSysOptionName = (): string => selectedItem?.displayName as string;
    
    return (
        <>
         <PluginWrapper>
            <ToolbarWrapper>
                <SysOptionsCommandPanel
                     edit={{
                        disabled: isDisabledEdit(),
                        onClick: () => {
                            setEditModal(true)
                        }
                    }}
                    refresh={{
                        onClick: handleRefresh
                    }}
                    
                    search={{
                        placeholder: t("sysOptions.searchPlaceholder"),
                        value: searchValue,
                        onChange: handleSearchChange,
                        onSearch: (value) => handleSearchChange(value),
                    }}
                    permission={props.plugin.permission}
                />
            </ToolbarWrapper>

            <div className={styles.gridWrapper}>
                <div className={styles.masterGrid}>
                    {
                        isSubmitting ? 
                            <Spinner /> 
                        :
                            <DefaultGrid
                                showQuantity
                                separator
                                gridId={props.gridId}
                                data={data}
                                dataProvider={sysOptionsDataProvider}
                                totalCount={totalCount}
                                filter={gridFilter}
                                hiddenPagination={undefined}
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                onSelect={(row) => {
                                    setSelectedItem(row)
                                    row ? pluginCtx.masterGrid.onSelectEvent(row,DocumentType.sysOptions) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onSort={(i) => {
                                    dispatchGridFilter({ type: "sort", payload: i.propertyName })
                                }}
                                onFilterDelete={(i) => {
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })
                                }}
                                onPageNumberChange={(n) => {
                                    dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })
                                }}
                                onNumberPerPageChange={(n) => {
                                    dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })
                                }}
                            />

                    }
                </div>                        
            </div>
            
            {
                <div className={styles.detailsTabView}>
                {showDetailsTabs && detailsTabsPanel.tabs.length > 0 && selectedItem && (
                    <>
                        <div className={tabsStyles.tabsPanelWrapper}>
                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                <TabsPanel
                                    id="detailsTabsPanel"
                                    activeId={detailsTabsPanel.currentTab?.id}
                                    tabs={detailsTabsPanel.tabs}
                                    onActive={(id) => {
                                        dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                    }}
                                ></TabsPanel>
                            </div>
                        </div>
                        <div className={tabsStyles.contentWrapper}>
                            {detailsTabsPanel.tabs.map((item) => {
                                return (
                                    <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                        {item.view.content}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
            }

            {
                editModal &&
                <SysOptionsEditModal                       
                    cancel={() => {
                        setEditModal(false)
                        isRealRefresh.current = true;
                        setViewState('refresh')
                        setSelectedItem(undefined)
                    }}
                    idGlobal={selectedItem?.idGlobal}
                    lockFromPermission={lockFromPermission}
                    name={getSysOptionName()}
                />
            }

         </PluginWrapper>
        </>
    )
}